/**
 * Cliente HTTP tipado — placeholder até o gerador OpenAPI rodar.
 *
 * Em produção, este arquivo é substituído por client gerado a partir do
 * /docs/json do NestJS (seção 10.3 do blueprint). Mantemos a API leve aqui
 * para o app funcionar antes da geração.
 */

const BASE = import.meta.env.VITE_API_BASE_URL ?? "/api";

export interface RequestOptions {
  token?: string;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

export class ApiError extends Error {
  constructor(public readonly status: number, message: string, public readonly body: unknown) {
    super(message);
    this.name = "ApiError";
  }
}

const TOKEN_KEY = "pro-roller.access_token";

/**
 * Quando o backend devolve 401 fora do fluxo de login, a sessão expirou: o
 * frontend não tem como recuperar — limpa o token e força um hard reload pra
 * `/login`. Ignoramos `/auth/login` (senha errada também é 401) pra não criar
 * loop. Também ignoramos páginas que já são públicas (já estamos em `/login`).
 */
function handleSessionExpired(path: string) {
  if (path.startsWith("/auth/login")) return;
  if (typeof window === "undefined") return;
  if (window.location.pathname.startsWith("/login")) return;
  localStorage.removeItem(TOKEN_KEY);
  // Hard reload — descarta todo o estado da SPA, ProtectedRoute decide pra
  // onde mandar (cai em /login porque não há mais token).
  window.location.assign("/login");
}

async function request<T>(
  path: string,
  init: RequestInit,
  opts: RequestOptions = {},
): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(opts.headers ?? {}),
  };
  if (opts.token) headers["Authorization"] = `Bearer ${opts.token}`;

  const resp = await fetch(`${BASE}${path}`, {
    ...init,
    headers,
    signal: opts.signal,
  });

  const text = await resp.text();
  const body: unknown = text ? safeJSON(text) : null;
  if (!resp.ok) {
    const message =
      isErrorBody(body) && body.message
        ? Array.isArray(body.message)
          ? body.message.join(", ")
          : body.message
        : `HTTP ${resp.status}`;
    if (resp.status === 401) handleSessionExpired(path);
    throw new ApiError(resp.status, message, body);
  }
  return body as T;
}

function safeJSON(s: string): unknown {
  try {
    return JSON.parse(s);
  } catch {
    return s;
  }
}

function isErrorBody(b: unknown): b is { message?: string | string[] } {
  return typeof b === "object" && b !== null && "message" in (b as Record<string, unknown>);
}

/** Baixa um arquivo autenticado (PDF etc.) e dispara o download no navegador — sem diálogo de impressão. */
export async function baixarArquivo(path: string, nomePadrao: string, opts: RequestOptions = {}): Promise<void> {
  const headers: Record<string, string> = { ...(opts.headers ?? {}) };
  if (opts.token) headers["Authorization"] = `Bearer ${opts.token}`;
  const resp = await fetch(`${BASE}${path}`, { headers, signal: opts.signal });
  if (!resp.ok) {
    const body = safeJSON(await resp.text());
    if (resp.status === 401) handleSessionExpired(path);
    throw new ApiError(resp.status, isErrorBody(body) && body.message ? String(body.message) : `HTTP ${resp.status}`, body);
  }
  const disp = resp.headers.get("Content-Disposition") ?? "";
  const nome = /filename="?([^";]+)"?/.exec(disp)?.[1] ?? nomePadrao;
  const url = URL.createObjectURL(await resp.blob());
  const a = document.createElement("a");
  a.href = url;
  a.download = nome;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export const apiClient = {
  get: <T>(path: string, opts?: RequestOptions) => request<T>(path, { method: "GET" }, opts),
  post: <T>(path: string, body: unknown, opts?: RequestOptions) =>
    request<T>(path, { method: "POST", body: JSON.stringify(body) }, opts),
  patch: <T>(path: string, body: unknown, opts?: RequestOptions) =>
    request<T>(path, { method: "PATCH", body: JSON.stringify(body) }, opts),
  put: <T>(path: string, body: unknown, opts?: RequestOptions) =>
    request<T>(path, { method: "PUT", body: JSON.stringify(body) }, opts),
  delete: <T>(path: string, opts?: RequestOptions & { body?: unknown }) =>
    request<T>(
      path,
      { method: "DELETE", ...(opts?.body !== undefined ? { body: JSON.stringify(opts.body) } : {}) },
      opts,
    ),
};
